// src/pages/Lounge/MemberProfileOverView/MemberProfileOverView.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import i18n from "@/i18n";

import cls from "./MemberProfileOverView.module.scss";

import avatarPng from "@/assets/UI/avatar.png";
import api from "@/utils/axios";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import Loading from "@/components/Loading/Loading";

import manIcon from "@/assets/UI/man.webp";
import womanIcon from "@/assets/UI/woman.webp";
import non_binaryIcon from "@/assets/UI/non_binary.webp";
import trans_manIcon from "@/assets/UI/trans_man.webp";
import trans_womanIcon from "@/assets/UI/trans_woman.webp";
import genderfluidIcon from "@/assets/UI/genderfluid.webp";
import self_describeIcon from "@/assets/UI/self_describe.webp";

import { ATTRACTION_GENDERS } from "@/enums/lifestyleEnums";
//#region About Us Details
const GENDER_ICON_MAP = {
  man: manIcon,
  woman: womanIcon,
  non_binary: non_binaryIcon,
  trans_man: trans_manIcon,
  trans_woman: trans_womanIcon,
  genderfluid: genderfluidIcon,
  self_describe: self_describeIcon,
  not_select: self_describeIcon,
};

const SECONDARY_LANGUAGE_LABELS = {
  en: "English",
  fr: "Français",
  si: "සිංහල",
  ta: "தமிழ்",
  hi: "हिन्दी",
};

function getGenderIcon(gender) {
  return GENDER_ICON_MAP[gender] || self_describeIcon;
}

function asArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
}

const ATTRACTION_ORDER = ATTRACTION_GENDERS;

function buildAttractionNodes(keys, clsLocal) {
  if (!keys || !keys.length) return null;

  const ordered = ATTRACTION_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.attraction.labels.${key}`);
    return (
      <span key={key} className={clsLocal.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

//#endregion About Us Details

const safeStr = (v) => (v == null ? "" : String(v));

const getVideoIdFromPost = (post) => {
  const v = post?.videoId;
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?._id) return v._id;
  return "";
};

const MemberProfileOverView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [friendsPreview, setFriendsPreview] = useState([]);
  const [friendsTotal, setFriendsTotal] = useState(0);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [friendsError, setFriendsError] = useState("");
  const [clubsPreview, setClubsPreview] = useState([]);
  const [clubsTotal, setClubsTotal] = useState(0);
  const [clubsLoading, setClubsLoading] = useState(false);
  const [clubsError, setClubsError] = useState("");
  const [publicPosts, setPublicPosts] = useState([]);
  const [publicPostsTotal, setPublicPostsTotal] = useState(0);
  const [publicPostsLoading, setPublicPostsLoading] = useState(false);
  const [publicPostsError, setPublicPostsError] = useState("");
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  //Action Path
  const handleEditProfile = () => {
    navigate(`/lounge/my-profile/edit`);
  };
  const handleEditAvatar = () => {
    navigate(`/lounge/my-profile/edit/member-info`);
  };

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get("/v1/my/profile-overview");
        if (!ignore) {
          setOverview(data || null);
        }
      } catch (err) {
        console.error(err);
        if (!ignore) {
          setError(
            err?.response?.data?.message ||
              t("memberProfile.states.error", "Something went wrong."),
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [t]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setClubsLoading(true);
        setClubsError("");
        const { data } = await api.get("/v1/lounge/my-clubs/preview", {
          params: { limit: 6 },
        });

        if (ignore) return;

        const list = data?.clubs || [];
        setClubsPreview(list);
        setClubsTotal(data?.total ?? list.length);
      } catch (err) {
        console.error(err);
        if (!ignore) {
          setClubsError(
            err?.response?.data?.message ||
              t("memberProfileOverview.clubs.error"),
          );
        }
      } finally {
        if (!ignore) {
          setClubsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [t]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setPublicPostsLoading(true);
        setPublicPostsError("");
        const { data } = await api.get("/v1/posts/my-public-feed", {
          params: { page: 1, limit: 15 },
        });

        if (ignore) return;

        const rows =
          data?.rows || data?.posts || data?.data || data?.items || [];

        setPublicPosts(Array.isArray(rows) ? rows : []);
        setPublicPostsTotal(data?.meta?.total ?? rows.length);
      } catch (err) {
        console.error(err);
        if (!ignore) {
          setPublicPostsError(
            err?.response?.data?.message ||
              t("memberProfileOverview.posts.error"),
          );
        }
      } finally {
        if (!ignore) {
          setPublicPostsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [t]);
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setFriendsLoading(true);
        setFriendsError("");

        // Adjust URL if your route is different
        const { data } = await api.get("/v1/friends", {
          params: { page: 1 },
        });

        if (ignore) return;

        const list = data?.friends || [];
        setFriendsPreview(list.slice(0, 6)); // only first 6 friends
        setFriendsTotal(data?.total ?? list.length);
      } catch (err) {
        console.error(err);
        if (!ignore) {
          setFriendsError(
            err?.response?.data?.message ||
              t("memberProfileOverview.friends.error"),
          );
        }
      } finally {
        if (!ignore) {
          setFriendsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [t]);

  const handleOpenFriendProfile = (memberId) => {
    if (!memberId) return;
    navigate(`/lounge/member-profile/${memberId}`);
  };

  //#region variables for Member details
  const user = overview?.user || null;
  const member = overview?.member || null;
  const personal = member?.personal || null;
  const lifestyle = member?.lifestyle || null;
  const primaryPartner = member?.primaryPartner || null;
  const partnerPersonal = primaryPartner?.personal || null;
  const partnerLifestyle = primaryPartner?.lifestyle || null;

  const memberType = member?.memberType || "single";
  const isCouple = memberType === "couple";

  const avatarSrc = member?.avatar?.url || avatarPng;

  const username = user?.username || t("memberProfileOverview.displayName");
  const memberAge = user?.age ?? null;
  const partnerAge = partnerPersonal?.age ?? null;

  const rawGender = personal?.gender || "not_select";
  const rawPartnerGender = partnerPersonal?.gender || null;

  const genderIconSrc = getGenderIcon(rawGender);
  const partnerGenderIconSrc = rawPartnerGender
    ? getGenderIcon(rawPartnerGender)
    : null;

  const city = personal?.city || null;
  const country = personal?.country || null;
  const cityCountry =
    city && country ? `${city}, ${country}` : city || country || null;

  const secondaryLanguageCode = member?.secondaryLanguage || null;

  const secondaryLanguageLabel =
    secondaryLanguageCode && secondaryLanguageCode !== "en"
      ? SECONDARY_LANGUAGE_LABELS[secondaryLanguageCode] ||
        secondaryLanguageCode
      : null;

  //#endregion variables for Member details

  // #region Description helpers
  const genderKey = rawGender || "not_select";
  const partnerGenderKey = rawPartnerGender || "not_select";

  const genderPhraseRaw = t(`memberProfile.description.gender.${genderKey}`);
  const partnerGenderPhraseRaw = t(
    `memberProfile.description.gender.${partnerGenderKey}`,
  );

  const singleLanguageFragment = secondaryLanguageLabel
    ? t("memberProfile.description.single.languageFragment", {
        language: secondaryLanguageLabel,
      })
    : "";

  const coupleLanguageFragment = secondaryLanguageLabel
    ? t("memberProfile.description.couple.languageFragment", {
        language: secondaryLanguageLabel,
      })
    : "";

  const relationshipTypeKey = primaryPartner?.relationshipType || null;
  const relationshipStatusKey = primaryPartner?.relationshipStatus || null;

  const relationshipTypePhrase = relationshipTypeKey
    ? t(`memberProfile.description.relationshipType.${relationshipTypeKey}`)
    : t("memberProfile.description.relationshipType.default");

  const relationshipStatusPhrase = relationshipStatusKey
    ? t(`memberProfile.description.relationshipStatus.${relationshipStatusKey}`)
    : t("memberProfile.description.relationshipStatus.default");

  const hasLocation = !!cityCountry;

  const descriptionKey = isCouple
    ? hasLocation
      ? "memberProfile.description.couple.withLocation"
      : "memberProfile.description.couple.noLocation"
    : hasLocation
      ? "memberProfile.description.single.withLocation"
      : "memberProfile.description.single.noLocation";

  const memberGenderWithAge =
    memberAge != null ? `${genderPhraseRaw} (${memberAge})` : genderPhraseRaw;

  const partnerGenderWithAge =
    isCouple && partnerGenderPhraseRaw
      ? partnerAge != null
        ? `${partnerGenderPhraseRaw} (${partnerAge})`
        : partnerGenderPhraseRaw
      : partnerGenderPhraseRaw;

  const descriptionValues = isCouple
    ? {
        relationshipTypePhrase,
        relationshipStatusPhrase,
        cityCountry: cityCountry || "",
        languageFragment: coupleLanguageFragment || "",
        genderPhrase: memberGenderWithAge,
        partnerGenderPhrase: partnerGenderWithAge,
      }
    : {
        genderPhrase: memberGenderWithAge,
        cityCountry: cityCountry || "",
        languageFragment: singleLanguageFragment || "",
      };

  // === Meta pill content ===
  const profileTypeText = isCouple
    ? t("memberProfile.member.memberType.couple")
    : t("memberProfile.member.memberType.single");

  // ==== Sexual identity & role helpers (from MemberProfile) ====
  const memberLifestyle = lifestyle || null;

  const memberSexualIdentityKey =
    memberLifestyle?.sexual_identity || "not_select";
  const memberSexualRoleKey = memberLifestyle?.sexual_role || "not_select";

  const memberIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${memberSexualIdentityKey}`,
  );
  const memberIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${memberSexualIdentityKey}`,
  );
  const memberRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${memberSexualRoleKey}`,
  );
  const memberRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${memberSexualRoleKey}`,
  );

  const partnerSexualIdentityKey =
    partnerLifestyle?.sexual_identity || "not_select";
  const partnerSexualRoleKey = partnerLifestyle?.sexual_role || "not_select";

  const partnerIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${partnerSexualIdentityKey}`,
  );
  const partnerIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${partnerSexualIdentityKey}`,
  );
  const partnerRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${partnerSexualRoleKey}`,
  );
  const partnerRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${partnerSexualRoleKey}`,
  );

  const hasMemberSexualData =
    memberSexualIdentityKey !== "not_select" ||
    memberSexualRoleKey !== "not_select";

  const hasPartnerSexualData =
    isCouple &&
    partnerLifestyle &&
    (partnerSexualIdentityKey !== "not_select" ||
      partnerSexualRoleKey !== "not_select");

  // ==== Attraction genders helpers (from MemberProfile) ====
  const memberAttractionKeys = asArray(memberLifestyle?.attraction_genders);
  const partnerAttractionKeys = asArray(partnerLifestyle?.attraction_genders);

  const hasMemberAttraction = memberAttractionKeys.length > 0;
  const hasPartnerAttraction = partnerAttractionKeys.length > 0;

  const memberAttractionNodes = buildAttractionNodes(memberAttractionKeys, cls);
  const partnerAttractionNodes = buildAttractionNodes(
    partnerAttractionKeys,
    cls,
  );

  // simple overview state
  const isReady = !loading && !error && overview;

  const renderFeedPost = (post) => {
    const type = safeStr(post?.type).toLowerCase();
    const hasImages = Array.isArray(post?.images) && post.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(post);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={post} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={post} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={post} />;

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };
  // #endregion Description helpers
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }
  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>

          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>

          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>

          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>

          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Header bar (avatar + name + actions) */}
        <header className={cls.header}>
          <div className={cls.headerMain}>
            <div className={cls.avatarWrap}>
              <img src={avatarSrc} alt={username} className={cls.avatar} />
              <span className={cls.badgeOnline}>
                {t("memberProfileOverview.badges.online")}
              </span>
            </div>

            <div className={cls.headerInfo}>
              {/* Display name = username */}
              <h1 className={cls.displayName}>{username}</h1>

              {/* Gender icons + member since */}
              <div className={cls.usernameRow}>
                <span className={cls.genderIconRow} aria-hidden="true">
                  {isCouple ? (
                    <>
                      <span className={cls.genderBadgeItem}>
                        <img
                          src={genderIconSrc}
                          alt={t(
                            "memberProfileOverview.genderIconAlt",
                            "Gender",
                          )}
                          className={cls.genderIconImage}
                        />
                      </span>
                      {partnerGenderIconSrc && (
                        <span className={cls.genderBadgeItem}>
                          <img
                            src={partnerGenderIconSrc}
                            alt={t(
                              "memberProfileOverview.partnerGenderIconAlt",
                              "Partner gender",
                            )}
                            className={cls.genderIconImage}
                          />
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={cls.genderBadgeItem}>
                      <img
                        src={genderIconSrc}
                        alt={t("memberProfileOverview.genderIconAlt", "Gender")}
                        className={cls.genderIconImage}
                      />
                    </span>
                  )}
                </span>

                {/* <span className={cls.dot} />

                {memberSinceLabel && (
                  <span className={cls.memberSince}>
                    {t("memberProfileOverview.joinedAt")} {memberSinceLabel}
                  </span>
                )} */}
              </div>

              {/* Meta pills */}
              <div className={cls.metaRow}>
                {cityCountry && (
                  <span className={`${cls.metaPill} ${cls.metaPillLocation}`}>
                    {cityCountry}
                  </span>
                )}

                <span className={`${cls.metaPill} ${cls.metaPillType}`}>
                  {profileTypeText}
                </span>
              </div>
            </div>
          </div>

          <div className={cls.headerActions}>
            {/* Change profile picture first */}
            <button
              type="button"
              className={`${cls.btnGhost} ${cls.btnChangeAvatar}`}
              onClick={handleEditAvatar}
            >
              {t("memberProfileOverview.actions.editPic")}
            </button>

            {/* Edit profile second */}
            <button
              type="button"
              className={cls.btnPrimary}
              onClick={handleEditProfile}
            >
              {t("memberProfileOverview.actions.editProfile")}
            </button>
          </div>
        </header>

        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge/my-profile"
            className={`${cls.tabButton} ${cls.activeTab}`}
          >
            {t("memberProfileOverview.tabs.timeline")}
          </Link>

          <Link to="/lounge/my-profile/about" className={cls.tabButton}>
            {t("memberProfileOverview.tabs.about")}
          </Link>

          <Link to="/lounge/friends" className={cls.tabButton}>
            {t("memberProfileOverview.tabs.friends")}
          </Link>
        </nav>

        {/* Error / loading simple states */}
        {loading && (
          <div className={cls.stateRow}>
            <span className={cls.stateDot} />
            <span className={cls.stateText}>
              {t("memberProfile.states.loading", "Loading profile...")}
            </span>
          </div>
        )}

        {!loading && error && (
          <div className={cls.stateRowError}>
            <span className={cls.stateText}>{error}</span>
          </div>
        )}

        {/* Main layout only when ready */}
        {isReady && (
          <div className={cls.layout}>
            {/* Left column */}
            <div className={cls.leftColumn}>
              {/* About / description + sexual + attraction */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("memberProfileOverview.about.title")}
                </h2>

                {/* Description (with highlight spans), same logic style as MemberProfile */}
                {descriptionKey && (
                  <div className={`${cls.block} ${cls.blockDescription}`}>
                    <p className={cls.descriptionText}>
                      <Trans
                        i18nKey={descriptionKey}
                        values={descriptionValues}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                    </p>
                  </div>
                )}

                {/* Sexual identity & role */}
                <div className={`${cls.block} ${cls.blockIntimacy}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.sexualDescription.titleCouple")
                      : i18n.t("memberProfile.sexualDescription.titleSingle")}
                  </h3>

                  {!memberLifestyle || !hasMemberSexualData ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.sexualDescription.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerSexualData ? (
                    // SINGLE (or no partner sexual data)
                    <p className={cls.sexDescription}>
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line1"
                        values={{
                          identityLabel: memberIdentityLabel,
                          identityText: memberIdentityText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                      <br />
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line2"
                        values={{
                          roleLabel: memberRoleLabel,
                          roleText: memberRoleText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                    </p>
                  ) : (
                    // COUPLE – two paragraphs: you + partner
                    <>
                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine1"
                          values={{
                            identityLabel: memberIdentityLabel,
                            identityText: memberIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine2"
                          values={{
                            roleLabel: memberRoleLabel,
                            roleText: memberRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>

                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine1"
                          values={{
                            identityLabel: partnerIdentityLabel,
                            identityText: partnerIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine2"
                          values={{
                            roleLabel: partnerRoleLabel,
                            roleText: partnerRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>

                {/* Attraction genders */}
                <div className={`${cls.block} ${cls.blockAttraction}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.attraction.titleCouple")
                      : i18n.t("memberProfile.attraction.titleSingle")}
                  </h3>

                  {!hasMemberAttraction ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.attraction.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerAttraction ? (
                    // SINGLE (or no partner attraction data)
                    <p className={cls.attrDescription}>
                      {i18n.t("memberProfile.attraction.single.prefix")}
                      {memberAttractionNodes}
                      {i18n.t("memberProfile.attraction.single.suffix")}
                    </p>
                  ) : (
                    // COUPLE – two paragraphs: you + partner
                    <>
                      <p className={cls.attrDescription}>
                        {i18n.t("memberProfile.attraction.couple.memberPrefix")}
                        {memberAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>

                      <p className={cls.attrDescription}>
                        {i18n.t(
                          "memberProfile.attraction.couple.partnerPrefix",
                        )}
                        {partnerAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>
                    </>
                  )}
                </div>
              </section>

              {/* Friends preview */}
              <section className={cls.card}>
                <div className={cls.cardHeaderRow}>
                  <div>
                    <h2 className={cls.cardTitle}>
                      {t("memberProfileOverview.friendsTitle")}
                    </h2>
                    <span className={cls.cardMeta}>
                      {t("memberProfileOverview.friendsCount", {
                        count: friendsTotal,
                      })}
                    </span>
                  </div>

                  {friendsTotal > 0 && (
                    <Link to="/lounge/friends" className={cls.linkButton}>
                      {t("memberProfileOverview.friendsViewAll")}
                    </Link>
                  )}
                </div>

                {friendsLoading && (
                  <p className={cls.cardMeta}>
                    {t("memberProfileOverview.friends.loading")}
                  </p>
                )}

                {!friendsLoading && friendsError && (
                  <p className={cls.cardMeta}>
                    {friendsError || t("memberProfileOverview.friends.error")}
                  </p>
                )}

                {!friendsLoading && !friendsError && friendsTotal === 0 && (
                  <p className={cls.emptyText}>
                    {t("memberProfileOverview.friends.empty")}
                  </p>
                )}

                {!friendsLoading &&
                  !friendsError &&
                  friendsPreview.length > 0 && (
                    <div className={cls.gridSmallThumbs}>
                      {friendsPreview.map((friend) => {
                        const friendUser = friend.user || {};
                        const friendMember = friend.member || {};
                        const thumbSrc = friendMember.avatar?.url || avatarPng;
                        const memberId = friendMember._id;

                        return (
                          <button
                            key={friend.id}
                            type="button"
                            className={cls.smallThumb}
                            onClick={() => handleOpenFriendProfile(memberId)}
                          >
                            <img
                              src={thumbSrc}
                              alt={
                                friendUser.username ||
                                t("memberProfileOverview.friendsAlt")
                              }
                              className={cls.smallThumbImg}
                            />
                            <span className={cls.smallThumbLabel}>
                              {friendUser.username ||
                                t("memberProfileOverview.friendsAlt")}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
              </section>

              {/* Groups preview */}
              <section className={cls.card}>
                <div className={cls.cardHeaderRow}>
                  <div>
                    <h2 className={cls.cardTitle}>
                      {t("memberProfileOverview.clubsTitle")}
                    </h2>
                    <span className={cls.cardMeta}>
                      {t("memberProfileOverview.clubsCount", {
                        count: clubsTotal,
                      })}
                    </span>
                  </div>

                  {clubsTotal > 0 && (
                    <Link to="/lounge/my-clubs" className={cls.linkButton}>
                      {t("memberProfileOverview.clubsViewAll")}
                    </Link>
                  )}
                </div>

                {clubsLoading && (
                  <p className={cls.cardMeta}>
                    {t("memberProfileOverview.clubs.loading")}
                  </p>
                )}

                {!clubsLoading && clubsError && (
                  <p className={cls.cardMeta}>
                    {clubsError || t("memberProfileOverview.clubs.error")}
                  </p>
                )}

                {!clubsLoading && !clubsError && clubsTotal === 0 && (
                  <p className={cls.emptyText}>
                    {t("memberProfileOverview.clubs.empty")}
                  </p>
                )}

                {!clubsLoading && !clubsError && clubsPreview.length > 0 && (
                  <div className={cls.gridSmallThumbs}>
                    {clubsPreview.map((club) => {
                      const clubAvatar =
                        club?.clubAvatarThumb?.url ||
                        club?.clubAvatar?.url ||
                        avatarPng;
                      const clubId = club?.clubId;

                      if (!clubId) return null;

                      return (
                        <Link
                          key={clubId}
                          to={`/lounge/clubs/single/${clubId}/home`}
                          className={`${cls.smallThumb} ${cls.smallThumbLink}`}
                        >
                          <img
                            src={clubAvatar}
                            alt={
                              club?.clubName ||
                              t("memberProfileOverview.clubsAlt")
                            }
                            className={cls.smallThumbImg}
                          />
                          <span className={cls.smallThumbLabel}>
                            {club?.clubName ||
                              t("memberProfileOverview.clubsAlt")}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            {/* Right column */}
            <div className={cls.rightColumn}>
              <section className={cls.card}>
                <div className={cls.cardHeaderRow}>
                  <div>
                    <h2 className={cls.cardTitle}>
                      {t("memberProfileOverview.postsTitle")}
                    </h2>
                    <span className={cls.cardMeta}>
                      {t("memberProfileOverview.postsCount", {
                        count: publicPostsTotal,
                      })}
                    </span>
                  </div>
                </div>

                {publicPostsLoading && (
                  <p className={cls.cardMeta}>
                    {t("memberProfileOverview.posts.loading")}
                  </p>
                )}

                {!publicPostsLoading && publicPostsError && (
                  <p className={cls.cardMeta}>
                    {publicPostsError || t("memberProfileOverview.posts.error")}
                  </p>
                )}

                {!publicPostsLoading &&
                  !publicPostsError &&
                  publicPostsTotal === 0 && (
                    <p className={cls.emptyText}>
                      {t("memberProfileOverview.posts.empty")}
                    </p>
                  )}

                {!publicPostsLoading &&
                  !publicPostsError &&
                  publicPosts.length > 0 && (
                    <div className={cls.postList}>
                      {publicPosts.map((post) => (
                        <React.Fragment key={post._id}>
                          {renderFeedPost(post)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberProfileOverView;
